/**
 * useWebSocket — maintains a single persistent WebSocket connection per authenticated session.
 * On push, dispatches to the react-query cache so pollers can be removed incrementally.
 *
 * Protocol:
 *   - Connect: wss://{VITE_WS_URL}?token={jwt}
 *   - Subscribe: send { action: 'subscribe', project_id, token } on connect and on project change
 *   - Push message: JobStatusPush — dispatched to cache via dispatchToCache()
 *   - On close: exponential backoff reconnect (1s→2s→4s→…→30s cap, full jitter)
 *   - After 5 consecutive failures: surface amber 'WebSocketOfflineBanner', stop reconnecting
 *     until user clicks Retry.
 *
 * No polling fallback. Degraded mode = banner + manual Retry.
 *
 * Token note: AuthContext does not expose the raw JWT. The REST client uses the Cognito
 * ID token (API Gateway Cognito Authorizer validates the ID token, not the access token —
 * see src/lib/api.ts), so we use the same ID token here for consistency. It is read fresh
 * from authLib on every connect/subscribe so reconnects pick up refreshed tokens.
 */
import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { getIdToken } from "@/lib/auth";

export interface JobStatusPush {
  type: "job_status";
  project_id: string;
  job_type:
    | "phase0_generation"
    | "artifact_chat_job"
    | "avoma_sync"
    | "intake_arrival";
  job_id: string;
  status:
    | "generating"
    | "pending"
    | "processing"
    | "syncing"
    | "complete"
    | "failed";
  payload?: Record<string, unknown>;
  updated_at: string;
}

/**
 * Additive message type for unified-chat streaming (P0 redesign CR item **E1**).
 *
 * Pushed by the async chat worker via the shared `by-project` fan-out, NOT by `ws-pusher` — it does
 * not go through the DynamoDB job-status table at all, because one write per narration delta would
 * cost more latency than the streaming saves.
 *
 * `project_id` is always the value the worker read from `project_chat_sessions`, never the one on
 * the SQS message (SEC-5). The client still filters on it defensively below.
 */
export interface AgentStreamPush {
  type: "agent_stream";
  project_id: string;
  /** The agent message row this turn writes to. */
  message_id: string;
  stage:
    | "routing"
    | "directive_resolution"
    | "drafting"
    | "validating"
    | "computing_diff"
    | "complete"
    | "failed"
    | "heartbeat"
    | "note_delta";
  artifact_type: string | null;
  /** Narration text. Present only on `note_delta`. */
  delta?: string;
  agent?: string;
  artifact_types?: string[];
  /** Monotonic within a turn. */
  seq: number;
  at: string;
}

type RealtimePush = JobStatusPush | AgentStreamPush;

// Exported so WebSocketOfflineBanner can trigger a retry.
// eslint-disable-next-line import/no-mutable-exports
export let retryWebSocket: (() => void) | null = null;

const MAX_RETRIES = 5;
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

function jitteredBackoff(attempt: number): number {
  const exp = Math.min(BASE_BACKOFF_MS * Math.pow(2, attempt), MAX_BACKOFF_MS);
  return Math.floor(Math.random() * exp); // full jitter
}

export function useWebSocket(projectId?: string): void {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const wsRef = useRef<WebSocket | null>(null);
  const retriesRef = useRef(0);
  const closedRef = useRef(false);
  const projectIdRef = useRef<string | undefined>(projectId);
  const offlineBannerShownRef = useRef(false);

  // Keep the project ref current without triggering reconnects.
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  const connect = useCallback((): void => {
    const wsUrl = import.meta.env.VITE_WS_URL as string | undefined;
    const tok = getIdToken();
    if (!wsUrl || wsUrl.includes("REPLACE_AFTER_DEPLOY") || !tok) return;

    const url = `${wsUrl}?token=${encodeURIComponent(tok)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      retriesRef.current = 0;
      offlineBannerShownRef.current = false;
      window.dispatchEvent(new CustomEvent("ws-online"));
      // Read projectIdRef at open time (most current value) — this closes the race between
      // connect() being called and onopen firing, since projectIdRef is kept current by its
      // own useEffect and may have advanced since connect() was scheduled.
      const pid = projectIdRef.current;
      console.log("[WS] opened, subscribing to", pid);
      if (pid) {
        ws.send(
          JSON.stringify({
            action: "subscribe",
            project_id: pid,
            token: getIdToken() ?? undefined,
          }),
        );
      }
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data as string) as RealtimePush;
        if (msg.type === "job_status") {
          dispatchToCache(queryClient, msg, navigate);
        } else if (msg.type === "agent_stream") {
          applyAgentStream(queryClient, msg);
        }
      } catch {
        /* malformed message — ignore */
      }
    };

    ws.onclose = () => {
      if (closedRef.current) return; // intentional close on unmount
      retriesRef.current += 1;
      if (retriesRef.current > MAX_RETRIES) {
        // Surface amber offline banner — no more automatic reconnects
        if (!offlineBannerShownRef.current) {
          offlineBannerShownRef.current = true;
          window.dispatchEvent(new CustomEvent("ws-offline"));
        }
        return;
      }
      const wait = jitteredBackoff(retriesRef.current);
      setTimeout(() => {
        if (!closedRef.current) connect();
      }, wait);
    };

    ws.onerror = () => {
      ws.close();
    }; // onclose handles reconnect
  }, [queryClient, navigate]);

  // Allow external retry (banner Retry button)
  useEffect(() => {
    retryWebSocket = () => {
      retriesRef.current = 0;
      offlineBannerShownRef.current = false;
      connect();
    };
    return () => {
      retryWebSocket = null;
    };
  }, [connect]);

  // Subscribe when project changes (without reconnecting).
  // If the socket is still CONNECTING when the project changes, wait for onopen to fire —
  // the onopen handler reads projectIdRef.current which is already updated by this point.
  // If the socket is OPEN, send subscribe immediately.
  useEffect(() => {
    const ws = wsRef.current;
    console.log("[WS] project changed", {
      projectId,
      readyState: ws?.readyState,
    });
    if (!ws || !projectId) return;
    if (ws.readyState === WebSocket.OPEN) {
      console.log("[WS] sending subscribe", { projectId });
      ws.send(
        JSON.stringify({
          action: "subscribe",
          project_id: projectId,
          token: getIdToken() ?? undefined,
        }),
      );
    }
    // If CONNECTING: onopen will fire and read the updated projectIdRef.current — no action needed.
    // If CLOSED/CLOSING: a reconnect is already scheduled; onopen will subscribe with the new projectId.
  }, [projectId]);

  // Main connect effect — only reconnect on auth change, not token refresh.
  useEffect(() => {
    if (!isAuthenticated) return;
    closedRef.current = false;
    connect();
    return () => {
      closedRef.current = true;
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
}

/**
 * Apply one `agent_stream` frame (CR items **E1** + **E7**).
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * THE TWO-KEY RULE — THIS IS WHAT MAKES REMOVING THE POLL SAFE
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Live state goes to `['project-chat-stream', projectId]`. The transcript at
 * `['project-chat', projectId]` is touched EXACTLY ONCE per turn, on the terminal stage, and only as
 * an invalidation.
 *
 * Writing stream state into the transcript key would recreate the race E7 exists to remove — any
 * fetch of the transcript (window focus, reconnect recovery, an accept mutation's invalidation)
 * would overwrite partial stream state with server truth mid-turn. Keeping them separate means the
 * two writers never contend, so there is no ordering to get right.
 *
 * ⛔ Do not "simplify" this by merging the keys, and do not add a periodic refetch anywhere.
 *
 * Documents never arrive here: the 128 KB `@connections` cap means the revised artifact is persisted
 * to `project_chat_message_results` and the terminal stage is the signal to go read it.
 */
function applyAgentStream(
  queryClient: ReturnType<typeof useQueryClient>,
  msg: AgentStreamPush,
): void {
  const key = ["project-chat-stream", msg.project_id];

  // `heartbeat` is a keep-alive for the 10-minute idle socket timeout (E8). It carries no pipeline
  // meaning, so it must never advance the stage — only refresh the timestamp.
  if (msg.stage === "heartbeat") {
    queryClient.setQueryData(key, (old: Record<string, unknown> | undefined) =>
      old ? { ...old, last_seen_at: msg.at } : old,
    );
    return;
  }

  queryClient.setQueryData(key, (old: Record<string, unknown> | undefined) => {
    const prev = (old ?? {}) as {
      message_id?: string;
      seq?: number;
      per_file?: Record<string, string>;
      notes?: Record<string, string>;
      stage?: string;
      artifact_types?: string[];
    };

    // A new turn resets state; the same turn's out-of-order frames are dropped. Frames are pushed
    // from concurrent fan-out branches, so arrival order is not guaranteed even on one socket.
    const sameTurn = prev.message_id === msg.message_id;
    if (sameTurn && (prev.seq ?? 0) >= msg.seq) return old;

    const base = sameTurn
      ? prev
      : { message_id: msg.message_id, per_file: {}, notes: {} };

    const perFile = { ...(base.per_file ?? {}) };
    const notes = { ...(base.notes ?? {}) };

    if (msg.stage === "note_delta") {
      if (msg.artifact_type && msg.delta) {
        notes[msg.artifact_type] = (notes[msg.artifact_type] ?? "") + msg.delta;
      }
    } else if (msg.artifact_type) {
      perFile[msg.artifact_type] = msg.stage;
    }

    const terminal = msg.stage === "complete" || msg.stage === "failed";
    // Turn-level stage only advances on turn-level frames. A per-file `drafting` must not overwrite
    // the turn stage, or a 3-file turn would appear to restart each time a branch begins.
    const turnStage =
      msg.artifact_type === null && msg.stage !== "note_delta"
        ? msg.stage
        : (base.stage ?? msg.stage);

    return {
      ...base,
      message_id: msg.message_id,
      stage: turnStage,
      per_file: perFile,
      notes,
      artifact_types: msg.artifact_types ?? base.artifact_types ?? [],
      ...(msg.agent ? { agent: msg.agent } : {}),
      seq: msg.seq,
      terminal: terminal && msg.artifact_type === null,
      last_seen_at: msg.at,
    };
  });

  // THE single transcript refetch per turn. The worker emits this stage only after
  // `finalizeAgentTurn` has committed, so this fetch cannot read a half-written turn.
  if (
    (msg.stage === "complete" || msg.stage === "failed") &&
    msg.artifact_type === null
  ) {
    void queryClient.invalidateQueries({
      queryKey: ["project-chat", msg.project_id],
    });
  }
}

/**
 * Maps each JobStatusPush to the matching react-query cache key and applies the update.
 * Uses TanStack Query v5 API.
 */
function dispatchToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  msg: JobStatusPush,
  navigate: ReturnType<typeof useNavigate>,
): void {
  switch (msg.job_type) {
    case "phase0_generation":
      applyPhase0Status(queryClient, msg);
      break;

    case "artifact_chat_job": {
      // artifact_type and job_id are in the payload
      const artifactType = (msg.payload?.artifact_type as string) ?? "unknown";
      queryClient.setQueryData(
        ["chat-job", msg.project_id, artifactType, msg.job_id],
        (old: Record<string, unknown> | undefined) => ({
          ...old,
          status: msg.status,
          ...msg.payload,
        }),
      );
      break;
    }

    case "avoma_sync": {
      void queryClient.invalidateQueries({
        queryKey: ["project-meetings", msg.project_id],
      });
      const n = (msg.payload?.newly_completed as number) ?? 0;
      if (n > 0 && msg.status === "complete") {
        toast.success(
          `Sync complete — ${n} meeting${n !== 1 ? "s" : ""} updated`,
        );
      }
      break;
    }

    case "intake_arrival":
      void queryClient.invalidateQueries({ queryKey: ["intakes"] });
      toast(
        `🆕 New intake: ${(msg.payload?.company_name as string) ?? "Unknown"}`,
        {
          action: {
            label: "View",
            onClick: () => navigate("/intakes"),
          },
        },
      );
      break;
  }
}

/**
 * Apply one `phase0_generation` push. The push carries only the status (and the run id), so it is
 * MERGED into the cached generation row for the same run and then confirmed with a refetch. It used
 * to REPLACE the row, dropping `error_message`, `artifacts` and `completed_at`, and a terminal status
 * also stopped the fallback poll, so the page could freeze on that thin object.
 */
export function applyPhase0Status(
  queryClient: ReturnType<typeof useQueryClient>,
  msg: JobStatusPush,
): void {
  const key = ["phase0-status", msg.project_id];
  queryClient.setQueryData(key, (old: Record<string, unknown> | undefined) => {
    if (!old) return old;
    const current = (old.generation ?? null) as Record<string, unknown> | null;
    // A push for an older run must not overwrite the newer run the page is showing.
    if (current?.id && current.id !== msg.job_id) return old;
    return { ...old, generation: { ...(current ?? {}), status: msg.status } };
  });
  void queryClient.invalidateQueries({ queryKey: key });

  if (msg.status === "complete") {
    void queryClient.invalidateQueries({ queryKey: ["artifact", msg.project_id] });
    toast.success("Phase 0 artifacts are ready");
  } else if (msg.status === "failed") {
    toast.error("Phase 0 generation failed. See the Phase 0 tab for details.");
  }
}
